import React, { useState } from 'react';
import {
    Alert,
    Anchor,
    Box,
    Button,
    Group,
    Menu,
    Modal,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconLayoutGrid, IconPhoto, IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import { listDeviceFetcher, type Device } from '../../data/Device';
import type { Asset } from '../../data/Asset';
import {
    createComponent,
    getComponent,
    reloadComponent,
    updateComponent,
    type PropEntry,
    type ShellComponent,
} from '../../data/ShellConfig';
import { useCsrf } from '../../hooks/useCsrf';
import { track } from '../../utils/mixpanel';
import { AnalyticsEvents } from '../../analytics/events';
import AssetPicker from './AssetPicker';
import PropEditor from './PropEditor';
import AssetDropzone from './studio/AssetDropzone';
import { isStaticImageUrl, isStaticVideoUrl } from './studio/studioUtils';
import { useRouterDrawerCloseOptional } from './RouterDrawer';

/** Where a component's URL comes from. */
type ComponentSourceMode = 'asset' | 'url';

const previewMediaStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
};

/**
 * Mirrors how the device shell and the scene preview decide what to render:
 * direct media URLs become <img>/<video>, everything else an iframe. Without
 * this branch a PNG would preview as the browser's standalone image document,
 * which is not what a screen actually shows.
 */
const SourcePreview: React.FC<{ url: string; label: string }> = ({ url, label }) => (
    <Box
        style={{
            width: '100%',
            height: 180,
            overflow: 'hidden',
            borderRadius: 8,
            border: '1px solid var(--mantine-color-gray-4)',
            background: '#000',
            position: 'relative',
        }}
    >
        {isStaticVideoUrl(url) ? (
            <video src={url} autoPlay muted loop playsInline style={previewMediaStyle} />
        ) : isStaticImageUrl(url) ? (
            <img src={url} alt={label} style={previewMediaStyle} />
        ) : (
            <iframe
                title="Component preview"
                src={url}
                style={{
                    width: '200%',
                    height: '200%',
                    border: 'none',
                    transform: 'scale(0.5)',
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                }}
            />
        )}
    </Box>
);

type ComponentFormProps = {
    /** When set, form loads and updates this component. Omit to create a new one. */
    componentId?: string;
    onSaved?: (component: ShellComponent) => void;
    /** Called after save when not inside a RouterDrawer. */
    onClose?: () => void;
    /** Show a link to open/create this component in the full Studio editor. */
    showStudioLink?: boolean;
};

const ComponentFormFields: React.FC<{
    initial: ShellComponent | null;
    componentId?: string;
    onSaved?: (component: ShellComponent) => void;
    onClose?: () => void;
    showStudioLink?: boolean;
}> = ({ initial, componentId, onSaved, onClose, showStudioLink = false }) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();
    const closeDrawer = useRouterDrawerCloseOptional();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [props, setProps] = useState<PropEntry[]>(initial?.props ?? []);
    const [deviceRefreshOpen, setDeviceRefreshOpen] = useState(false);
    const [refreshDeviceId, setRefreshDeviceId] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const isEdit = Boolean(componentId);
    const { data: devices = [] } = useSWR<Device[]>(isEdit ? 'api/devices' : null, listDeviceFetcher);

    // An existing component reopens in whichever mode built it; new ones default
    // to Image, since uploading is now the common path.
    const [sourceMode, setSourceMode] = useState<ComponentSourceMode>(
        initial ? (initial.assetId ? 'asset' : 'url') : 'asset'
    );
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(initial?.asset ?? null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const form = useForm({
        initialValues: {
            name: initial?.name ?? '',
            url: initial?.url ?? '',
        },
        validate: {
            name: v => (v.trim() ? null : 'Name is required'),
            // Format only. Whether a URL is *required* depends on the source mode,
            // which is tracked outside the form — see `sourceReady` below. Putting
            // that condition in here would depend on this closure being re-read on
            // every render, which is not something to rely on.
            url: v => {
                if (!v.trim()) return null;
                try {
                    new URL(v);
                    return null;
                } catch {
                    return 'Enter a valid URL';
                }
            },
        },
    });

    const url = form.values.url;
    let urlIsValid = false;
    try {
        if (url) {
            new URL(url);
            urlIsValid = true;
        }
    } catch {
        urlIsValid = false;
    }

    /** The chosen source is complete enough to save. */
    const sourceReady = sourceMode === 'asset' ? selectedAsset !== null : urlIsValid;
    const canSubmit = form.values.name.trim() !== '' && sourceReady;

    /** What the preview should show, whichever mode is active. */
    const previewUrl = sourceMode === 'asset' ? selectedAsset?.url ?? null : urlIsValid ? url : null;

    const handleSubmit = async (values: typeof form.values) => {
        if (!sourceReady) {
            setError(
                sourceMode === 'asset'
                    ? 'Choose an image for this component.'
                    : 'Enter a URL for this component.'
            );
            return;
        }

        setSaving(true);
        setError('');

        // Exactly one source goes to the server. In URL mode assetId is sent as
        // null explicitly, so switching away from an image clears the old link
        // rather than leaving the component pointing at both.
        const payload =
            sourceMode === 'asset'
                ? { name: values.name.trim(), assetId: selectedAsset!.id, props }
                : { name: values.name.trim(), url: values.url.trim(), assetId: null, props };

        try {
            const component = isEdit
                ? await updateComponent(componentId!, payload, csrfToken)
                : await createComponent(payload, csrfToken);
            track(isEdit ? AnalyticsEvents.ComponentUpdated : AnalyticsEvents.ComponentCreated, {
                componentId: component.id,
                sourceMode,
            });
            // Seed the detail cache with the freshly saved component so that
            // re-opening this form (via useSWR on `api/components/${id}`)
            // doesn't briefly show the stale pre-save snapshot before the
            // background revalidation catches up.
            await mutate(`api/components/${component.id}`, component, { revalidate: false });
            onSaved?.(component);
            if (closeDrawer) {
                closeDrawer();
            } else {
                onClose?.();
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save component');
        } finally {
            setSaving(false);
        }
    };

    const componentName = form.values.name.trim() || initial?.name || 'this component';

    const handleRefreshEverywhere = async () => {
        if (!componentId) return;
        try {
            await reloadComponent(componentId, null, csrfToken);
            track(AnalyticsEvents.ComponentReloadRequested, { scope: 'all_devices', componentId });
            notifications.show({
                title: 'Refresh requested',
                message: `Asked every device showing “${componentName}” to reload it.`,
            });
        } catch (err: unknown) {
            notifications.show({
                title: 'Refresh failed',
                message: err instanceof Error ? err.message : 'Could not request the refresh.',
                color: 'red',
            });
        }
    };

    const confirmDeviceRefresh = async () => {
        if (!componentId || !refreshDeviceId) return;
        setRefreshing(true);
        try {
            await reloadComponent(componentId, refreshDeviceId, csrfToken);
            track(AnalyticsEvents.ComponentReloadRequested, { scope: 'single_device', componentId });
            notifications.show({
                title: 'Refresh requested',
                message: 'The selected device was asked to reload this component.',
            });
            setDeviceRefreshOpen(false);
        } catch (err: unknown) {
            notifications.show({
                title: 'Refresh failed',
                message: err instanceof Error ? err.message : 'Could not request the refresh.',
                color: 'red',
            });
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
                {showStudioLink && (
                    <Anchor
                        component={Link}
                        to={`/studio/components/${isEdit ? componentId : 'new'}`}
                        onClick={() => onClose?.()}
                        size="sm"
                        underline="hover"
                    >
                        <Group gap={4} wrap="nowrap" c="violet">
                            <IconLayoutGrid size={14} />
                            {isEdit ? 'Open in Studio' : 'Create in the Studio'}
                        </Group>
                    </Anchor>
                )}

                <TextInput
                    withAsterisk
                    label="Component Name"
                    placeholder="e.g. Live Odds Panel"
                    {...form.getInputProps('name')}
                />
                <Stack gap="xs">
                    <Text size="sm" fw={500}>
                        Content
                    </Text>
                    <SegmentedControl
                        value={sourceMode}
                        onChange={value => setSourceMode(value as ComponentSourceMode)}
                        data={[
                            { label: 'Image', value: 'asset' },
                            { label: 'URL', value: 'url' },
                        ]}
                    />

                    {sourceMode === 'asset' ? (
                        selectedAsset ? (
                            <Group justify="space-between" wrap="nowrap">
                                <Text size="sm" c="dimmed" lineClamp={1}>
                                    {selectedAsset.name}
                                </Text>
                                <Group gap="xs" wrap="nowrap">
                                    <Button
                                        variant="subtle"
                                        size="compact-sm"
                                        leftSection={<IconPhoto size={14} />}
                                        onClick={() => setPickerOpen(true)}
                                    >
                                        Change
                                    </Button>
                                </Group>
                            </Group>
                        ) : (
                            <Stack gap="xs">
                                {/* The form itself is a drop target, so dragging a
                                    file straight onto it uploads and selects in one
                                    gesture rather than two. */}
                                <AssetDropzone compact onUploaded={setSelectedAsset} />
                                <Button variant="light" onClick={() => setPickerOpen(true)}>
                                    Choose from your library
                                </Button>
                            </Stack>
                        )
                    ) : (
                        <TextInput
                            withAsterisk
                            label="URL"
                            placeholder="https://partner.example.com/panel"
                            {...form.getInputProps('url')}
                        />
                    )}
                </Stack>

                {previewUrl && (
                    <SourcePreview url={previewUrl} label={form.values.name || 'Component preview'} />
                )}

                <PropEditor props={props} onChange={setProps} />

                {error && (
                    <Alert color="red" icon={<IconExclamationCircle />} title="Error">
                        {error}
                    </Alert>
                )}

                <Group justify="flex-end" wrap="nowrap">
                    {isEdit && (
                        <Menu withinPortal position="bottom-end" shadow="md">
                            <Menu.Target>
                                <Button
                                    variant="light"
                                    color="gray"
                                    leftSection={<IconRefresh size={16} />}
                                >
                                    Refresh
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item onClick={() => void handleRefreshEverywhere()}>
                                    Refresh everywhere
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() => {
                                        setRefreshDeviceId(null);
                                        setDeviceRefreshOpen(true);
                                    }}
                                >
                                    Refresh on device…
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    )}
                    <Button type="submit" loading={saving} disabled={!canSubmit}>
                        {isEdit ? 'Update Component' : 'Save Component'}
                    </Button>
                </Group>
            </Stack>

            <AssetPicker
                opened={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={setSelectedAsset}
                selectedAssetId={selectedAsset?.id ?? null}
            />

            {isEdit && (
                <Modal
                    opened={deviceRefreshOpen}
                    onClose={() => !refreshing && setDeviceRefreshOpen(false)}
                    title="Refresh on a device"
                    centered
                >
                    <Stack gap="md">
                        <Text size="sm">
                            Ask a single device to reload{' '}
                            <Text span fw={600}>{componentName}</Text>. Other devices showing it are
                            left untouched.
                        </Text>
                        <Select
                            label="Device"
                            placeholder="Select a device"
                            searchable
                            data={devices.map(d => ({ value: d.id, label: d.name }))}
                            value={refreshDeviceId}
                            onChange={setRefreshDeviceId}
                        />
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                onClick={() => setDeviceRefreshOpen(false)}
                                disabled={refreshing}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => void confirmDeviceRefresh()}
                                loading={refreshing}
                                disabled={!refreshDeviceId}
                            >
                                Refresh
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            )}
        </form>
    );
};

const ComponentForm: React.FC<ComponentFormProps> = ({
    componentId,
    onSaved,
    onClose,
    showStudioLink,
}) => {
    const { data, error, isLoading } = useSWR(
        componentId ? `api/components/${componentId}` : null,
        () => getComponent(componentId!)
    );

    if (componentId && isLoading) {
        return <Text c="dimmed">Loading…</Text>;
    }
    if (componentId && (error || !data)) {
        return (
            <Alert color="red" icon={<IconExclamationCircle />} title="Error">
                Could not load component.
            </Alert>
        );
    }

    // Remount when loaded so useForm initialValues apply.
    return (
        <ComponentFormFields
            key={componentId ?? 'new'}
            initial={data ?? null}
            componentId={componentId}
            onSaved={onSaved}
            onClose={onClose}
            showStudioLink={showStudioLink}
        />
    );
};

export default ComponentForm;
